import React, { useState, useEffect, useCallback } from 'react';
import { FromSelector } from './FromSelector';
import { ToField } from './ToField';
import { MessageThread } from './MessageThread';
import { ComposeBar } from './ComposeBar';
import { useDialpadSms } from '../../../hooks/support/useDialpadSms';
import { getBranchSmsNumber, getBranchForNumber } from '../../../config/dialpadBranches';
import { getSmsDraft, setSmsDraftFrom } from './draftStore';
import type { ContactPhone } from './types';

/** Normalize a phone number to its last 10 digits for a stable draft key. */
const draftKeyForPhone = (phone?: string): string =>
  (phone || '').replace(/\D/g, '').slice(-10);

/** Check if a phone string is a valid US phone number (10 digits, or 11 starting with 1). */
export const isValidUsPhone = (phone?: string): boolean => {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return true;
  return digits.length === 10 && /^[2-9]/.test(digits);
};

export interface SmsExchangeProps {
  /** Pre-populated phone numbers to show in the To dropdown */
  contactPhones?: ContactPhone[];
  /** External phone number to default to */
  externalPhone?: string;
  /** Whether to show the To field (hidden = use externalPhone directly) */
  showToField?: boolean;
  /** Allow searching for additional contacts beyond contactPhones */
  allowContactSearch?: boolean;
  /** Allow typing an arbitrary phone number in the To field */
  allowManualEntry?: boolean;
  /** Default branch for send-from */
  defaultBranch?: string;
  /** Callback when message count changes (for parent badge display) */
  onMessageCount?: (count: number) => void;
  /** Called after an SMS is successfully sent, with the recipient phone */
  onSmsSent?: (recipientPhone: string) => void;
  /** Compact mode -- less padding, smaller text */
  compact?: boolean;
  /**
   * The component fills its container edge to edge, and its bands (From row,
   * compose row) use the container's own 16px horizontal gutter.
   *
   * For a host that already provides the card and its padding. Without it, the
   * From row's grey fill sits inset from the card edges on every side — a
   * floating grey rectangle rather than a header band — because the host pads
   * the body and this component then paints its background inside that padding.
   */
  flush?: boolean;
}

/**
 * SmsExchange - Reusable SMS conversation component.
 *
 * Used across Support, CRM, Quoting, and Collections modules
 * to provide a consistent SMS experience.
 *
 * Features:
 * - From selector (department lines + personal line)
 * - Optional To field with pre-populated contacts and search typeahead
 * - Message thread display (inbound left/gray, outbound right/blue)
 * - Compose bar with Ctrl+Enter send and AI Draft button
 * - Auto-scroll to newest messages
 * - Thread switches when a different To phone is selected
 *
 * @example
 * ```tsx
 * // Support inbox (inline, compact, no To field)
 * <SmsExchange externalPhone={item.phone} defaultBranch={item.branch} compact />
 *
 * // Lead detail (CRM)
 * <SmsExchange
 *   contactPhones={[
 *     { phone: lead.phone, label: `${lead.primaryContactName} (Primary)` },
 *     ...lead.additionalContacts?.map(c => ({ phone: c.phone, label: c.name })) ?? [],
 *   ]}
 *   showToField
 *   allowContactSearch
 *   defaultBranch={lead.branch}
 * />
 *
 * // Collections
 * <SmsExchange
 *   contactPhones={collectionItem.contacts.map(c => ({
 *     phone: c.phone,
 *     label: `${c.name} (${c.role})`,
 *   }))}
 *   showToField
 *   defaultBranch="Seaside"
 * />
 * ```
 */
export function SmsExchange({
  contactPhones = [],
  externalPhone: externalPhoneProp,
  showToField = false,
  allowContactSearch = false,
  allowManualEntry = false,
  defaultBranch,
  onSmsSent,
  onMessageCount,
  compact = false,
  flush = false,
}: SmsExchangeProps) {
  // Resolve default "from" number from branch code
  const defaultFromNumber = defaultBranch
    ? getBranchSmsNumber(defaultBranch) ?? ''
    : '';

  // Default "to" number: externalPhone prop, or first contactPhone if available
  const defaultToNumber =
    externalPhoneProp ?? contactPhones[0]?.phone ?? '';
  const [toNumber, setToNumber] = useState(defaultToNumber);

  // Restore the previously selected from-number for this conversation (in-memory).
  const [fromNumber, setFromNumber] = useState(
    () => getSmsDraft(draftKeyForPhone(defaultToNumber))?.fromNumber ?? defaultFromNumber
  );

  // The effective external phone: when showToField is false, use prop directly
  const effectiveExternalPhone = showToField ? toNumber : externalPhoneProp;

  // Stable per-conversation key for draft persistence (body + from-number).
  const draftKey = draftKeyForPhone(effectiveExternalPhone);

  // When the conversation changes, restore that conversation's saved from-number.
  useEffect(() => {
    const saved = getSmsDraft(draftKey)?.fromNumber;
    if (saved) setFromNumber(saved);
  }, [draftKey]);

  // Persist the selected from-number so it survives navigating away and back.
  useEffect(() => {
    if (draftKey && fromNumber) setSmsDraftFrom(draftKey, fromNumber);
  }, [draftKey, fromNumber]);

  // Sync prop changes for non-ToField mode
  useEffect(() => {
    if (externalPhoneProp && !showToField) {
      setToNumber(externalPhoneProp);
    }
  }, [externalPhoneProp, showToField]);

  // Set default from number when branch changes
  useEffect(() => {
    if (defaultFromNumber && !fromNumber) {
      setFromNumber(defaultFromNumber);
    }
  }, [defaultFromNumber, fromNumber]);

  // Fetch thread -- thread key is based on normalized to phone number
  const { messages, isLoading, sendSmsAsync, isSending } = useDialpadSms(
    effectiveExternalPhone,
    defaultBranch || ''
  );

  // Notify parent of message count changes
  useEffect(() => {
    onMessageCount?.(messages.length);
  }, [messages.length, onMessageCount]);

  // Resolves true only once the server confirmed the send; the composer keeps the typed text until then.
  // The failure message itself is shown by the hook.
  const handleSend = useCallback(
    async (body: string): Promise<boolean> => {
      if (!isValidUsPhone(effectiveExternalPhone)) {
        return false;
      }
      const branch = getBranchForNumber(fromNumber) || defaultBranch || '';
      try {
        await sendSmsAsync({ text: body, branch });
      } catch {
        return false;
      }
      // The text HAS gone. A failing callback must not make the composer keep it as if it had not.
      try {
        if (effectiveExternalPhone) onSmsSent?.(effectiveExternalPhone);
      } catch (callbackError) {
        console.error('[SmsExchange] onSmsSent failed after a confirmed send:', callbackError);
      }
      return true;
    },
    [sendSmsAsync, fromNumber, defaultBranch, effectiveExternalPhone, onSmsSent]
  );

  const canCompose = !!(getBranchForNumber(fromNumber) || defaultBranch) && !!effectiveExternalPhone && isValidUsPhone(effectiveExternalPhone);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header controls — a band across the container when `flush`, so it reads
          as a header rather than a floating grey rectangle. */}
      <div
        className={`
          border-b border-slate-200 bg-slate-50 space-y-2
          ${flush ? 'px-4 py-2.5' : compact ? 'p-2' : 'p-3'}
        `}
      >
        <FromSelector
          value={fromNumber}
          onChange={setFromNumber}
          compact={compact}
        />
        {showToField && (
          <ToField
            value={toNumber}
            onChange={setToNumber}
            contactPhones={contactPhones}
            allowContactSearch={allowContactSearch}
            allowManualEntry={allowManualEntry}
            compact={compact}
          />
        )}
      </div>

      {/* Message thread */}
      <MessageThread
        messages={messages}
        isLoading={isLoading}
        compact={compact}
        flush={flush}
      />

      {/* Compose area */}
      <ComposeBar
        onSend={handleSend}
        isSending={isSending}
        disabled={!canCompose}
        compact={compact}
        flush={flush}
        draftKey={draftKey}
      />
    </div>
  );
}
